/**
 * Où renvoyer la personne après une connexion.
 *
 * Le parcours cassé : on prépare un panier sur une fiche magasin, une action
 * demande de se connecter, on saisit son numéro, on reçoit l'OTP — et on
 * atterrit sur `/home`. Le panier est intact côté serveur, mais la page qu'on
 * était en train d'utiliser a disparu, et il faut refaire le chemin à la main.
 *
 * La cause : `/login` et `/register` faisaient `router.push("/home")` en dur, et
 * aucun des trois points d'entrée vers la connexion ne transmettait d'où l'on
 * venait.
 */

/** Paramètre de requête portant la destination de retour. */
export const NEXT_PARAM = "next";

/** Destination par défaut quand on ne sait pas d'où vient la personne. */
export const DEFAULT_NEXT = "/home";

/**
 * Chemins vers lesquels on ne renvoie jamais après connexion : y retourner
 * relancerait le parcours d'authentification en boucle.
 */
const NEVER_RETURN_TO = ["/login", "/register", "/logout", "/onboarding"];

/**
 * Valide une destination de retour et la ramène à un chemin interne.
 *
 * Renvoyer l'utilisateur vers une valeur d'URL non validée est une redirection
 * ouverte : `?next=https://exemple.test` enverrait quelqu'un qui vient de se
 * connecter sur un site tiers, depuis un lien qui a l'air d'être le nôtre. On
 * n'accepte donc qu'un chemin absolu du même site — jamais de schéma, jamais
 * d'hôte, et pas de `//` initial (que le navigateur lit comme un hôte).
 */
export function safeNextPath(raw: string | null | undefined): string {
  if (!raw) return DEFAULT_NEXT;

  let value = raw.trim();
  if (!value) return DEFAULT_NEXT;

  // Une valeur encodée peut arriver telle quelle selon le point d'entrée.
  try {
    value = decodeURIComponent(value);
  } catch {
    return DEFAULT_NEXT;
  }

  // Les caractères de contrôle peuvent servir à contourner les contrôles ci-dessous.
  if (/[ -]/.test(value)) return DEFAULT_NEXT;
  if (!value.startsWith("/")) return DEFAULT_NEXT;
  if (value.startsWith("//") || value.startsWith("/\\")) return DEFAULT_NEXT;

  const path = value.split(/[?#]/)[0];
  if (NEVER_RETURN_TO.some((p) => path === p || path.startsWith(`${p}/`))) {
    return DEFAULT_NEXT;
  }

  return value;
}

/** Chemin courant, requête comprise — ce vers quoi il faudra revenir. */
export function currentPath(): string {
  if (typeof window === "undefined") return DEFAULT_NEXT;
  return `${window.location.pathname}${window.location.search}`;
}

/** `/login?next=…` construit depuis un chemin de retour. */
export function loginHref(returnTo?: string): string {
  const target = safeNextPath(returnTo ?? currentPath());
  if (target === DEFAULT_NEXT) return "/login";
  return `/login?${NEXT_PARAM}=${encodeURIComponent(target)}`;
}
